import type { ProgramItem } from "@tih/shared";
import {
  defaultProgramCopy,
  EVENT_DETAILS_DEFAULTS,
  type EventDetailsFormState,
} from "../../lib/event-details-form";

type Props = {
  value: EventDetailsFormState;
  onChange: (next: EventDetailsFormState) => void;
  disabled?: boolean;
  open?: boolean;
  onToggle?: () => void;
};

function ProgramRow({
  item,
  index,
  disabled,
  onChange,
  onRemove,
}: {
  item: ProgramItem;
  index: number;
  disabled?: boolean;
  onChange: (index: number, patch: Partial<ProgramItem>) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="admin-program-row">
      <label>
        <span>Time</span>
        <input
          type="text"
          value={item.time}
          disabled={disabled}
          placeholder="9:30 PM"
          onChange={(e) => onChange(index, { time: e.target.value })}
        />
      </label>
      <label className="admin-program-row-title">
        <span>Title</span>
        <input
          type="text"
          value={item.title}
          disabled={disabled}
          placeholder="Gathering & Opening Prayer"
          onChange={(e) => onChange(index, { title: e.target.value })}
        />
      </label>
      <label className="admin-program-row-note">
        <span>Note</span>
        <input
          type="text"
          value={item.note}
          disabled={disabled}
          placeholder="Optional"
          onChange={(e) => onChange(index, { note: e.target.value })}
        />
      </label>
      <button
        type="button"
        className="btn btn-ghost admin-program-remove"
        disabled={disabled}
        onClick={() => onRemove(index)}
        aria-label="Remove program item"
      >
        ×
      </button>
    </div>
  );
}

export function EventDetailsFields({
  value,
  onChange,
  disabled = false,
  open = true,
  onToggle,
}: Props) {
  function patch(partial: Partial<EventDetailsFormState>) {
    onChange({ ...value, ...partial });
  }

  function toggleCustomProgram(enabled: boolean) {
    if (enabled && value.program.length === 0) {
      patch({ useCustomProgram: true, program: defaultProgramCopy() });
    } else {
      patch({ useCustomProgram: enabled });
    }
  }

  function updateProgram(index: number, row: Partial<ProgramItem>) {
    const program = value.program.map((p, i) => (i === index ? { ...p, ...row } : p));
    patch({ program });
  }

  function removeProgram(index: number) {
    patch({ program: value.program.filter((_, i) => i !== index) });
  }

  function addProgram() {
    patch({
      program: [...value.program, { time: "", title: "", note: "" }],
    });
  }

  return (
    <div className="admin-event-details">
      <button
        type="button"
        className="admin-event-details-toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>Event details</span>
        <span className="admin-event-details-toggle-hint">
          Doors, venue, program — overrides site defaults for this date
        </span>
        <span className="admin-event-details-chevron">{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="admin-event-details-body">
          <div className="admin-form-row">
            <label>
              <span>Doors</span>
              <input
                type="text"
                value={value.doors}
                disabled={disabled}
                placeholder={EVENT_DETAILS_DEFAULTS.doors}
                onChange={(e) => patch({ doors: e.target.value })}
              />
            </label>
            <label>
              <span>Duration</span>
              <input
                type="text"
                value={value.duration}
                disabled={disabled}
                placeholder={EVENT_DETAILS_DEFAULTS.duration}
                onChange={(e) => patch({ duration: e.target.value })}
              />
            </label>
          </div>

          <fieldset className="admin-event-details-venue">
            <legend>Venue</legend>
            <label>
              <span>Name</span>
              <input
                type="text"
                value={value.venueName}
                disabled={disabled}
                placeholder={EVENT_DETAILS_DEFAULTS.venueName}
                onChange={(e) => patch({ venueName: e.target.value })}
              />
            </label>
            <label>
              <span>Address</span>
              <input
                type="text"
                value={value.venueAddress}
                disabled={disabled}
                placeholder={EVENT_DETAILS_DEFAULTS.venueAddress}
                onChange={(e) => patch({ venueAddress: e.target.value })}
              />
            </label>
            <label>
              <span>Coordinates (optional)</span>
              <input
                type="text"
                value={value.venueCoords}
                disabled={disabled}
                placeholder={EVENT_DETAILS_DEFAULTS.venueCoords}
                onChange={(e) => patch({ venueCoords: e.target.value })}
              />
            </label>
            <label>
              <span>Maps search query (optional)</span>
              <input
                type="text"
                value={value.venueMapsQuery}
                disabled={disabled}
                placeholder="Venue name, Kampala, Uganda"
                onChange={(e) => patch({ venueMapsQuery: e.target.value })}
              />
            </label>
            <label>
              <span>Directions URL (optional)</span>
              <input
                type="url"
                value={value.venueMapsUrl}
                disabled={disabled}
                placeholder="https://www.google.com/maps/dir/?api=1&destination=…"
                onChange={(e) => patch({ venueMapsUrl: e.target.value })}
              />
            </label>
            <label>
              <span>Map embed URL (optional)</span>
              <input
                type="url"
                value={value.venueEmbedUrl}
                disabled={disabled}
                placeholder="https://www.google.com/maps?q=…&output=embed"
                onChange={(e) => patch({ venueEmbedUrl: e.target.value })}
              />
            </label>
            <p className="admin-muted" style={{ marginTop: 8 }}>
              Leave venue fields empty to hide the directions map on the public landing
              (poster can still say venue TBD). Do not invent coordinates.
            </p>
          </fieldset>

          <div className="admin-event-details-program">
            <label className="admin-accent-live-toggle">
              <input
                type="checkbox"
                checked={value.useCustomProgram}
                disabled={disabled}
                onChange={(e) => toggleCustomProgram(e.target.checked)}
              />
              <span>Custom program for this event</span>
            </label>
            {value.useCustomProgram && (
              <div className="admin-program-list">
                {value.program.map((item, index) => (
                  <ProgramRow
                    key={index}
                    item={item}
                    index={index}
                    disabled={disabled}
                    onChange={updateProgram}
                    onRemove={removeProgram}
                  />
                ))}
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={disabled}
                  onClick={addProgram}
                >
                  + Add program item
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
